import { createSelector } from 'reselect'

import { filterAndSortContentStatistics } from 'apps/performanceAnalytics/store/lib/filterAndSortContentStatistics'
import { pickBestFlow } from 'apps/performanceAnalytics/store/lib/pickBestFlow'
import { AutomationFilter } from 'apps/performanceAnalytics/store/types'
import {
  CombinedStatistics,
  ContentAnalyticsContentType,
} from 'shared/api/requests/analytics/schemas'

const getState = (state: RootState) => state.analytics
const getQuery = (state: RootState) => getState(state).query
const getPeriod = (state: RootState) => getQuery(state).period
const getContentStatisticsList = (state: RootState) => getState(state).contentStatistics.list
const getIsNextPageAvailable = (state: RootState) =>
  getState(state).contentStatistics.nextPage !== -1

const getIsFilterlessFullyLoaded = (state: RootState) =>
  getState(state).contentStatistics.isFilterlessFullyLoaded

const getStatisticsSummary = (state: RootState) => getState(state).summary.data

const getContentDetails = (state: RootState) => getState(state).contentDetails

const getMediaByItem = (state: RootState, item: CombinedStatistics) => {
  const { posts, stories } = getContentDetails(state)
  const id = item.content.igmedia_id

  return item.content.post_type === ContentAnalyticsContentType.STORY ? stories[id] : posts[id]
}

const getUseIndividualContentLoading = (state: RootState) =>
  getContentDetails(state).useIndividualContentLoading

const getContentFlows = (state: RootState) => getState(state).contentFlows

const getFlowByItem = createSelector(
  [
    (state: RootState, item: CombinedStatistics) => getContentFlows(state),
    (_, item: CombinedStatistics) => item,
  ],
  (contentFlows, item: CombinedStatistics) => {
    if (item.automation) {
      return null
    }

    const flows = contentFlows.flows[item.content.igmedia_id] ?? []

    return pickBestFlow(flows)
  },
)

const getContentStatistics = createSelector(
  [getContentStatisticsList, getQuery, getIsFilterlessFullyLoaded],
  (list, query, isFilterlessFullyLoaded) =>
    isFilterlessFullyLoaded ? filterAndSortContentStatistics(list, query) : list,
)

const getIsContentLoaded = (state: RootState) =>
  getState(state).contentStatistics.isLoaded && getState(state).summary.isLoaded

const getIsContentLoading = (state: RootState) =>
  getState(state).contentStatistics.isLoading ||
  getState(state).contentStatistics.isLoadingNextPage ||
  getState(state).summary.isLoading

const getHasContentStatistics = (state: RootState) =>
  getState(state).contentStatistics.isLoaded && getState(state).contentStatistics.list.length > 0

const getIsFiltered = (state: RootState) => {
  const query = getQuery(state)
  return query.contentType !== null || query.automation !== AutomationFilter.ALL
}

export const AnalyticsSelectors = {
  getState,
  getQuery,
  getIsNextPageAvailable,
  getIsFilterlessFullyLoaded,
  getHasContentStatistics,
  getIsFiltered,
  getContentStatistics,
  getStatisticsSummary,
  getContentDetails,
  getMediaByItem,
  getUseIndividualContentLoading,
  getContentFlows,
  getFlowByItem,
  getIsContentLoaded,
  getIsContentLoading,
  getPeriod,
}
